// In-memory album-art thumbnail store — the reason native players (Apple
// Music etc.) never show cover pop-in: every row's thumb sits DECODED-READY
// in RAM and paints synchronously with the row. Here: 64px JPEG data URIs
// (~3KB each, ~15MB for a 4.5k library) fetched in batches from the app's
// local /album-art-thumbs endpoint, kept for the whole session. Rows read
// the map synchronously at render; no per-row request ever happens at
// scroll time once primed.
//
// Map values: dataURI string = art; null = track cached as "no embedded
// art" (render the default cover, never re-ask); absent = not primed yet
// (row falls back to the per-image /album-art?id=&s=1 URL and the caller
// re-primes later while the backend warmer fills its disk cache).

const _thumbs = new Map(); // trackId (number) -> dataURI | null

export function getArtThumb(id) {
    return _thumbs.get(id);
}

/**
 * Fetch thumbs for the given track ids in chunks. Already-known ids are
 * skipped, so re-calling with the same list is cheap and converges as the
 * backend warms more art. `onProgress()` fires after each chunk that added
 * anything (callers bump a version to re-render visible rows).
 * Returns how many ids are STILL unknown (0 = fully primed).
 */
export async function primeArtThumbs(ids, onProgress) {
    const missing = [];
    for (const id of ids) {
        if (id != null && !_thumbs.has(id)) missing.push(id);
    }
    const CHUNK = 500;
    for (let i = 0; i < missing.length; i += CHUNK) {
        const chunk = missing.slice(i, i + CHUNK);
        try {
            const r = await fetch(`http://localhost:3069/album-art-thumbs?ids=${chunk.join(',')}`);
            const { thumbs } = await r.json();
            let added = 0;
            for (const [id, d] of Object.entries(thumbs || {})) {
                if (!_thumbs.has(Number(id))) added++;
                _thumbs.set(Number(id), d);
            }
            if (added && onProgress) onProgress();
        } catch (e) { /* local server briefly down — next round retries */ }
    }
    let unknown = 0;
    for (const id of ids) { if (id != null && !_thumbs.has(id)) unknown++; }
    return unknown;
}
